import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import CompanySidebar, { CompanySectionKey } from '@/components/CompanySidebar'
import CompanyDetailView from '@/components/CompanyDetailView'
import { BackButton, Spinner } from '@rr/ui'
import { usePlatformCompany } from '@/modules/platform/hooks/usePlatformCompanies'

/**
 * Company detail page.
 *
 * Loads the platform company via `usePlatformCompany(id)` and threads the
 * real name through to the sidebar + detail sections. CompanyDetailView
 * currently expects `{ id, name }`; if its surface widens later we can
 * pass `company` directly.
 */
export default function CompanyDetailPage() {
  const params = useParams()
  const id = String(params.id)
  const navigate = useNavigate()
  const [active, setActive] = useState<CompanySectionKey>('clients')

  const { data, isLoading, isError, error } = usePlatformCompany(id)
  const apiCompany = (data as any) ?? null
  const name: string =
    apiCompany?.name ??
    apiCompany?.legalName ??
    (isLoading ? 'Loading…' : `Company ${id}`)

  const company = { id, name }

  return (
    <div className="page-pad">
      <div className="max-w-7xl mx-auto">
        <div className="flex gap-6">
          {/* Left Sidebar (desktop) */}
          <aside className="hidden md:block w-64 shrink-0">
            <div className="bg-card border border-border rounded-lg p-4">
              <CompanySidebar
                company={company}
                active={active}
                onChange={setActive}
              />
            </div>
          </aside>

          {/* Right main content */}
          <main className="flex-1">
            <div className="mb-4">
              <BackButton
                label="Back to organizations"
                onBack={() => navigate('/tenants/organizations')}
              />
            </div>

            {/* Mobile section selector */}
            <div className="md:hidden mb-4">
              <MobileSectionSelect active={active} onChange={setActive} />
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-16 bg-card rounded-lg border border-border">
                <Spinner />
              </div>
            ) : isError ? (
              <div className="bg-card rounded-lg border border-border p-6 text-sm text-muted-foreground">
                {error instanceof Error
                  ? error.message
                  : 'Failed to load company.'}
              </div>
            ) : (
              <CompanyDetailView company={company} activeSection={active} />
            )}
          </main>
        </div>
      </div>
    </div>
  )
}

function MobileSectionSelect({
  active,
  onChange,
}: {
  active: CompanySectionKey
  onChange: (k: CompanySectionKey) => void
}) {
  const items: { key: CompanySectionKey; label: string }[] = [
    { key: 'clients', label: 'Clients' },
    { key: 'carriers', label: 'Carriers' },
    { key: 'drivers', label: 'Fleet — Drivers' },
    { key: 'vehicles', label: 'Fleet — Vehicles' },
    { key: 'users', label: 'Users' },
    { key: 'quotes', label: 'Quotes' },
    { key: 'orders', label: 'Orders' },
    { key: 'cargos', label: 'Cargos' },
    { key: 'departments', label: 'Departments' },
    { key: 'addresses', label: 'Addresses' },
    { key: 'regions', label: 'Regions' },
    { key: 'quoteSources', label: 'Quote sources' },
    { key: 'clientTypes', label: 'Client types' },
    { key: 'entityRatings', label: 'Entity ratings' },
    { key: 'refusalReasons', label: 'Refusal reasons' },
    { key: 'contactPositions', label: 'Contact positions' },
    { key: 'roles', label: 'Roles' },
    { key: 'contacts', label: 'Contacts' },
  ]
  return (
    <select
      value={active}
      onChange={(e) => onChange(e.target.value as CompanySectionKey)}
      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
    >
      {items.map((it) => (
        <option key={it.key} value={it.key}>
          {it.label}
        </option>
      ))}
    </select>
  )
}
